import React from 'react';
import { Select } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { C } from '../../constants/design-tokens';
import { useResponsiveSizes } from '../../../../hooks/use-responsive-sizes';
import { useAuthContext } from '../../../../context/auth';
import { getAvatarsPlacas } from '../../../../clients/avatars-placas-registry';
import { CustomTooltip } from '../../../../components/form/fields';

/** Sentinel para el value del Ant Select cuando el user elige "Sin placa". */
const NO_PLACA_VALUE = '';

interface PlacaSelectorProps {
  selected: string | null;
  onChange: (id: string | null) => void;
}

/**
 * Dropdown para elegir la placa de cierre (outro) del proyecto. Las opciones
 * salen de `getAvatarsPlacas(clientId)?.outros`. Cada cliente puede tener las
 * suyas.
 *
 * Siempre incluye "Sin placa" como primera opción (default),
 * cuando el cliente no tiene placas configuradas, el selector se oculta.
 */
export const PlacaSelector: React.FC<PlacaSelectorProps> = ({ selected, onChange }) => {
  const { spacing, fontSize } = useResponsiveSizes();
  const { userInfo } = useAuthContext();
  const clientId = userInfo?.['custom:clientId'];

  const placas = clientId ? getAvatarsPlacas(clientId) : undefined;
  const outros = placas?.outros ?? [];
  if (outros.length === 0) return null;

  const options = [
    { value: NO_PLACA_VALUE, label: 'Sin placa' },
    ...outros.map(o => ({ value: o.id, label: o.label }))
  ];

  // Preview PNG hermano del video wide (mismo path + .png).
  const selectedOutro = outros.find(o => o.id === selected);
  const previewImageUrl = selectedOutro?.files.wide?.videoUrl?.replace(/\.mp4$/, '.gif');

  return (
    <div style={{ marginBottom: spacing.lg }}>
      <div style={{ marginBottom: spacing.sm }}>
        <span
          style={{
            fontSize: fontSize.sm,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: 0.6,
            color: C.textLabel
          }}
        >
          Placa de cierre
        </span>
      </div>
      <div style={{ display: 'flex', gap: spacing.xs, alignItems: 'center' }}>
        <Select<string>
          value={selected ?? NO_PLACA_VALUE}
          onChange={v => onChange(v === NO_PLACA_VALUE ? null : v)}
          options={options}
          style={{ flex: 1 }}
        />
        {previewImageUrl && (
          <CustomTooltip field={{ tooltipImage: previewImageUrl }}>
            <InfoCircleOutlined style={{ fontSize: 18, color: C.textSecondary, cursor: 'pointer' }} />
          </CustomTooltip>
        )}
      </div>
    </div>
  );
};
